import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, MapPin, Search, Menu, X, CreditCard } from 'lucide-react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import ProfileModal from './ProfileModal';
import AuthModal from './AuthModal';
import axios from 'axios';

export default function Header() {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileCity, setProfileCity] = useState('');
  const [userPhone, setUserPhone] = useState(localStorage.getItem('userPhone') || ''); 

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDrop, setShowSearchDrop] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [siteName, setSiteName] = useState('કાગજબુક ઓનલાઈન');

  useEffect(() => {
    // Listen for setting changes
    const handleSettingsUpdate = (e) => {
      if (e.detail && e.detail.site_name) {
        setSiteName(e.detail.site_name);
      }
    };
    window.addEventListener('siteSettingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('siteSettingsUpdated', handleSettingsUpdate);
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/settings`)
      .then(res => {
        if (res.data && res.data.site_name) {
          setSiteName(res.data.site_name);
        }
      })
      .catch(err => console.log('Settings fetch error'));
  }, [API_URL]);

  useEffect(() => {
    const phone = userPhone;
    if (phone) {
      fetch(`${API_URL}/api/profile/${phone}`)
        .then(res => res.json())
        .then(data => {
          if (data && data.name && data.name !== 'મહેમાન (Guest)') {
            setProfileName(data.name);
            setProfileCity(data.city);
          } else {
            setProfileName('');
            setProfileCity('');
          }
        })
        .catch(err => console.log('Profile fetch error'));
    } else {
        setProfileName('');
        setProfileCity('');
    }
  }, [API_URL, userPhone]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchDrop(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchQuery.length > 1) {
      axios.get(`${API_URL}/api/subservices?q=${encodeURIComponent(searchQuery)}`)
        .then(res => {
          setSearchResults(res.data);
          setShowSearchDrop(true);
        })
        .catch(err => console.error(err));
    } else {
      setSearchResults([]);
      setShowSearchDrop(false);
    }
  }, [searchQuery, API_URL]);

  const handleProfileUpdate = (updatedProfile) => {
    setProfileName(updatedProfile.name === 'મહેમાન (Guest)' ? '' : updatedProfile.name);
    setProfileCity(updatedProfile.city);
    if (updatedProfile.phone && !localStorage.getItem('userPhone')) {
      localStorage.setItem('userPhone', updatedProfile.phone);
      setUserPhone(updatedProfile.phone);
    }
  };

  useEffect(() => {
    // Listen for custom event to open auth modal from other components
    const handleOpenAuth = () => setIsAuthOpen(true);
    window.addEventListener('openAuthModal', handleOpenAuth);
    return () => window.removeEventListener('openAuthModal', handleOpenAuth);
  }, []);

  const handleLoginSuccess = (phone) => {
    setUserPhone(phone);
    setIsAuthOpen(false);
    
    // Check if there is an intended destination saved
    const intendedDest = sessionStorage.getItem('intended_dest');
    if (intendedDest) {
      sessionStorage.removeItem('intended_dest');
      navigate(intendedDest);
      return; // Profile will load asynchronously
    }
    
        // Optionally open profile modal if they are a new user to fill details
    setIsProfileOpen(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userPhone');
    setUserPhone('');
    setProfileName('');
    setProfileCity('');
  };

  const handleSearchResultClick = (sub) => {
    setShowSearchDrop(false);
    setSearchQuery('');
    if (!localStorage.getItem('userToken')) {
      sessionStorage.setItem('intended_dest', `/category/${encodeURIComponent(sub.category_name)}?sub=${encodeURIComponent(sub.title)}`);
      setIsAuthOpen(true);
      return;
    }
    navigate(`/category/${encodeURIComponent(sub.category_name)}?sub=${encodeURIComponent(sub.title)}`);
  };

  return (
    <div className="relative z-50">
      <header className="bg-[#F5B800] text-[#172554] px-4 md:px-8 lg:px-12 py-3 flex flex-col md:grid md:grid-cols-3 md:items-center relative z-50">
        
        {/* Left Section (Mobile: Top Row, Desktop: Grid Left) */}
        <div className="flex items-center justify-between w-full md:w-auto md:justify-start">
          <div className="flex items-center gap-3 shrink-0">
            {/* Hamburger Menu Icon (Mobile Only) */}
            <button 
              className="md:hidden p-1 text-[#172554] hover:bg-black/10 rounded-lg z-50 shrink-0"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
            <div className="cursor-pointer hover:opacity-90 transition-opacity flex items-center shrink-0" onClick={() => navigate('/')} data-testid="header-logo">
              <img 
                src={process.env.REACT_APP_LOGO_URL} 
                alt="Logo" 
                className="h-10 md:h-14 lg:h-16 w-auto object-contain"
              />
            </div>
            
            <Link to="/green-card" className="ml-1 bg-green-600 hover:bg-green-700 text-white font-bold py-1.5 px-2 md:px-3 rounded border border-green-500 transition-colors text-gujarati text-xs md:text-sm hidden md:flex items-center gap-1.5 shadow-sm max-[768px]:hidden" title="ગ્રીન કાર્ડ" data-testid="header-green-card-btn">
              <CreditCard className="w-4 h-4 md:w-4 md:h-4 shrink-0" />
              <span className="hidden md:inline-block">ગ્રીન કાર્ડ</span>
            </Link>
          </div>

          {/* Title on Mobile Only - Rendered inside left section to keep it inline */}
          <div className="md:hidden flex-1 flex justify-center w-full min-w-0 px-2 cursor-pointer" onClick={() => navigate('/')}>
            <h1 className="text-base sm:text-lg md:text-xl font-bold tracking-wide text-gujarati text-center leading-tight mx-auto transition-colors truncate">
              {siteName}
            </h1>
          </div>
          {/* Mobile: compact city chip on far right of header top row */}
          <button
            type="button"
            data-testid="header-city-btn-mobile"
            onClick={() => {
              if (userPhone) setIsProfileOpen(true);
              else setIsAuthOpen(true);
            }}
            className="md:hidden flex items-center gap-1 bg-white text-[#172554] px-2 py-1 rounded-md border border-[#172554]/30 hover:bg-[#FFF8DB] transition-colors font-medium text-gujarati text-[11px] shrink-0 shadow-sm max-w-[90px]"
          >
            <MapPin className="w-3.5 h-3.5 text-red-500 shrink-0" />
            <span className="truncate">{profileCity || 'શહેર'}</span>
          </button>
        </div>
        
        {/* Center Section (Desktop Only: Title) */}
        <div className="hidden md:flex justify-center items-center w-full group cursor-pointer" onClick={() => navigate('/')}>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-wide text-gujarati text-center leading-tight mx-auto group-hover:text-black transition-colors pb-1">
            {siteName}
          </h1>
        </div>

        {/* Right Section (Desktop Only: Search + Profile) */}
        <div className="hidden md:flex items-center justify-end gap-2 relative z-50">
          {/* Global Search - Desktop */}
          <div className="relative mr-4" ref={searchRef}>
              <div className="flex items-center bg-white rounded-full px-3 py-1.5 border border-[#172554]/20 focus-within:border-[#172554] transition-colors">
                <Search className="w-4 h-4 text-[#172554]/70" />
                <input 
                  type="text" 
                  placeholder="સેવા શોધો..." 
                  className="bg-transparent border-none outline-none text-[#172033] placeholder:text-[#64748B] text-sm ml-2 w-32 md:w-48 text-gujarati font-normal"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
              {showSearchDrop && (
                <div className="absolute top-full mt-2 w-[300px] right-0 bg-white rounded-lg shadow-lg border border-[#E2E8F0] overflow-hidden z-50">
                  {searchResults.length > 0 ? (
                    <ul className="max-h-[300px] overflow-auto">
                      {searchResults.map((res) => (
                        <li 
                          key={res.id} 
                          className="px-4 py-3 hover:bg-[#FFF8DB] cursor-pointer border-b border-[#E2E8F0] last:border-0"
                          onClick={() => handleSearchResultClick(res)}
                        >
                          <div className="text-[#172554] font-bold text-gujarati text-sm">{res.title}</div>
                          <div className="text-xs text-[#64748B] text-gujarati">{res.category_name}</div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="p-4 text-sm text-[#64748B] text-center text-gujarati">કોઈ પરિણામ મળ્યું નથી.</div>
                  )}
                </div>
              )}
            </div>
            
            <button 
              type="button"
              className="hidden md:flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity bg-white/20 p-2 rounded-lg pointer-events-auto"
              onClick={() => {
                if (userPhone) setIsProfileOpen(true);
                else setIsAuthOpen(true);
              }}
              id="header-profile-btn-id"
            >
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden border border-[#172554]/20 pointer-events-none">
                <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${profileName || 'user'}`} alt="User" className="w-full h-full pointer-events-none" />
              </div>
              <span className="text-gujarati font-medium pointer-events-none">{profileName || (userPhone ? 'પ્રોફાઇલ' : 'મહેમાન (Guest)')}</span>
            </button>
          {userPhone && (
            <button onClick={handleLogout} className="hidden md:block text-sm font-bold text-red-600 hover:text-red-700 ml-2 text-gujarati bg-white p-2 rounded-lg shrink-0">
              લૉગઆઉટ
            </button>
          )}

          <button
            type="button"
            data-testid="header-city-btn-desktop"
            onClick={() => {
              if (userPhone) setIsProfileOpen(true);
              else setIsAuthOpen(true);
            }}
            className="hidden md:flex items-center gap-1.5 ml-2 bg-white text-[#172554] px-3 py-2 rounded-lg border border-[#172554]/30 hover:bg-[#FFF8DB] hover:border-[#F5B800] transition-colors font-medium text-gujarati text-sm shrink-0 shadow-sm"
          >
            <MapPin className="w-4 h-4 lg:w-5 lg:h-5 text-red-500 shrink-0" />
            <span className="whitespace-nowrap">{profileCity || 'શહેર પસંદ કરો'}</span>
          </button>
        </div>

        {/* Mobile Search Bar - Full Width below title on mobile */}
        <div className="w-full mt-3 md:hidden relative z-40" ref={searchRef}>
           <div className="flex items-center bg-white rounded-lg px-4 py-2 border border-[#172554]/20 focus-within:border-[#172554] shadow-sm transition-colors w-full">
              <Search className="w-4 h-4 text-[#172554]/70" />
              <input 
                type="text" 
                placeholder="સેવા શોધો..." 
                className="bg-transparent border-none outline-none text-[#172033] placeholder:text-[#64748B] text-sm ml-2 w-full text-gujarati font-normal"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            {showSearchDrop && (
                <div className="absolute top-full mt-2 w-full left-0 bg-white rounded-lg shadow-lg border border-[#E2E8F0] overflow-hidden z-50">
                  {searchResults.length > 0 ? (
                    <ul className="max-h-[300px] overflow-auto">
                      {searchResults.map((res) => (
                        <li 
                          key={res.id} 
                          className="px-4 py-3 hover:bg-[#FFF8DB] cursor-pointer border-b border-[#E2E8F0] last:border-0"
                          onClick={() => handleSearchResultClick(res)}
                        >
                          <div className="text-[#172554] font-bold text-gujarati text-sm">{res.title}</div>
                          <div className="text-xs text-[#64748B] text-gujarati">{res.category_name}</div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="p-4 text-sm text-[#64748B] text-center text-gujarati">કોઈ પરિણામ મળ્યું નથી.</div>
                  )}
                </div>
              )}
        </div>
      </header>
        
      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-b shadow-2xl w-full absolute z-[100] left-0 top-full px-4 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          
          {/* User Profile in Mobile Menu */}
          <div className="flex items-center justify-between bg-blue-50/50 p-3 rounded-xl border border-blue-100 mb-2">
            <button 
              className="flex items-center gap-3 w-full"
              onClick={() => {
                setIsMobileMenuOpen(false);
                if (userPhone) setIsProfileOpen(true);
                else setIsAuthOpen(true);
              }}
            >
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center overflow-hidden shadow-sm shrink-0">
                <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${profileName || 'user'}`} alt="User" className="w-full h-full" />
              </div>
              <div className="flex flex-col text-left overflow-hidden w-full">
                <span className="text-[#172554] font-bold text-sm text-gujarati truncate">{profileName || (userPhone ? 'પ્રોફાઇલ' : 'મહેમાન (Guest)')}</span>
                {!userPhone && <span className="text-xs text-blue-600 font-medium truncate">લૉગિન કરો (Login)</span>}
              </div>
            </button>
          </div>
          
          <NavLink to="/" end onClick={() => setIsMobileMenuOpen(false)} className={({ isActive }) => `text-[#172554] font-semibold text-gujarati px-4 py-3 rounded-lg border transition-colors flex items-center gap-3 ${isActive ? 'bg-[#FFF8DB] border-[#F5B800] border-l-4' : 'border-gray-200 hover:bg-[#FFF8DB] hover:border-[#F5B800]'}`}>હોમ</NavLink>
          <NavLink to="/green-card" onClick={() => setIsMobileMenuOpen(false)} className={({ isActive }) => `text-green-700 font-bold text-gujarati px-4 py-3 rounded-lg border transition-colors flex items-center gap-3 ${isActive ? 'bg-green-50 border-green-500 border-l-4' : 'border-green-200 hover:bg-green-50 hover:border-green-400 bg-white'}`}><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 14H4v-4h11v4zm0-5H4V9h11v4zm5 5h-4V9h4v9z"/></svg>ગ્રીન કાર્ડ (Green Card)</NavLink>
          <NavLink to="/services" onClick={() => setIsMobileMenuOpen(false)} className={({ isActive }) => `text-[#172554] font-semibold text-gujarati px-4 py-3 rounded-lg border transition-colors flex items-center gap-3 ${isActive ? 'bg-[#FFF8DB] border-[#F5B800] border-l-4' : 'border-gray-200 hover:bg-[#FFF8DB] hover:border-[#F5B800]'}`}>સેવાઓ</NavLink>
          <NavLink 
            to="/my-applications" 
            onClick={(e) => {
              if (!userPhone) {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                sessionStorage.setItem('intended_dest', '/my-applications');
                setIsAuthOpen(true);
              } else {
                setIsMobileMenuOpen(false);
              }
            }} 
            className={({ isActive }) => `text-[#172554] font-semibold text-gujarati px-4 py-3 rounded-lg border transition-colors flex items-center gap-3 ${isActive ? 'bg-[#FFF8DB] border-[#F5B800] border-l-4' : 'border-gray-200 hover:bg-[#FFF8DB] hover:border-[#F5B800]'}`}
          >મારી અરજીઓ</NavLink>
          <NavLink 
            to="/my-documents" 
            onClick={(e) => {
              if (!userPhone) {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                sessionStorage.setItem('intended_dest', '/my-documents');
                setIsAuthOpen(true);
              } else {
                setIsMobileMenuOpen(false);
              }
            }} 
            className={({ isActive }) => `text-[#172554] font-semibold text-gujarati px-4 py-3 rounded-lg border transition-colors flex items-center gap-3 ${isActive ? 'bg-[#FFF8DB] border-[#F5B800] border-l-4' : 'border-gray-200 hover:bg-[#FFF8DB] hover:border-[#F5B800]'}`}
          >મારા દસ્તાવેજો</NavLink>
          <NavLink to="/track" onClick={() => setIsMobileMenuOpen(false)} className={({ isActive }) => `text-[#172554] font-semibold text-gujarati px-4 py-3 rounded-lg border transition-colors flex items-center gap-3 ${isActive ? 'bg-[#FFF8DB] border-[#F5B800] border-l-4' : 'border-gray-200 hover:bg-[#FFF8DB] hover:border-[#F5B800]'}`}>અરજીની સ્થિતિ તપાસો</NavLink>
          <NavLink to="/contact" onClick={() => setIsMobileMenuOpen(false)} className={({ isActive }) => `text-[#172554] font-semibold text-gujarati px-4 py-3 rounded-lg border transition-colors flex items-center gap-3 ${isActive ? 'bg-[#FFF8DB] border-[#F5B800] border-l-4' : 'border-gray-200 hover:bg-[#FFF8DB] hover:border-[#F5B800]'}`}>સંપર્ક/મદદ</NavLink>
          
          {userPhone && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button 
                onClick={() => {
                  handleLogout();
                  setIsMobileMenuOpen(false);
                }} 
                className="w-full text-center text-sm font-bold text-red-600 hover:bg-red-50 py-3 rounded-lg border border-red-100 transition-colors"
              >
                લૉગઆઉટ (Logout)
              </button>
            </div>
          )}
        </div>
      )}

      {/* Desktop Navigation */}
      <nav className="hidden md:block bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-2.5 flex items-center justify-center gap-2 lg:gap-3 flex-wrap">
          {[
            { to: '/', label: 'હોમ' },
            { to: '/services', label: 'સેવાઓ' },
            { to: '/my-applications', label: 'મારી અરજીઓ', requiresAuth: true },
            { to: '/my-documents', label: 'મારા દસ્તાવેજો', requiresAuth: true },
            { to: '/track', label: 'અરજીની સ્થિતિ તપાસો' },
            { to: '/contact', label: 'સંપર્ક/મદદ' },
          ].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              data-testid={`nav-${item.to.replace('/', '') || 'home'}`}
              onClick={(e) => {
                if (item.requiresAuth && !userPhone) {
                  e.preventDefault();
                  sessionStorage.setItem('intended_dest', item.to);
                  setIsAuthOpen(true);
                }
              }}
              className={({ isActive }) =>
                `text-[#172554] font-semibold text-gujarati text-sm lg:text-base px-4 lg:px-6 py-2 rounded-lg transition-colors shrink-0 border-b-[3px] ${
                  isActive
                    ? 'border-[#F5B800] bg-[#FFF8DB]/60'
                    : 'border-transparent hover:bg-[#FFF8DB] hover:border-[#F5B800]/40'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <ProfileModal 
        isOpen={isProfileOpen} 
        onClose={() => setIsProfileOpen(false)} 
        userPhone={userPhone}
        onProfileUpdate={handleProfileUpdate}
      />
      
      <AuthModal 
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  );
}